import React, { useState } from 'react';
import styled from 'styled-components';
import Link from 'next/link';
import { Colors } from 'planoplan-ui-kit/core/colors';
import { Mixins } from 'planoplan-ui-kit/core/mixins';
import { Button } from 'planoplan-ui-kit/core/button';
import { useTranslations } from '@libs/translations';
import { SearchField, Overlay } from '@components/Help-Center-Components';
import { Points, textSize } from '@styles';
import { IS_CLIENT, HELP_CENTER } from '@constants';

export const HelpCenterPage = ({ data }) => {
  const [isSearchActive, setIsSearchActive] = useState(false);
  const translations = useTranslations();
  const sections = data?.items || [];

  return (
    <Container isSearchActive={isSearchActive}>
      {!isSearchActive && (
        <TopWrap>
          <Title>{translations.t('site.help.title')}</Title>
          <Text>{translations.t('site.help.description')}</Text>
        </TopWrap>
      )}

      <SearchField isSearchActive={isSearchActive} searchActiveHandle={(isActive) => setIsSearchActive(isActive)} />
      {!isSearchActive && (
        <SectionWrap>
          {sections.map((section) => (
            <Link href={`/${HELP_CENTER.MAIN}/${section.slug}`} passHref key={section.slug}>
              <SectionCard as="a" aria-label={section.caption}>
                {section.icon && (
                  <SectionIcon src={section.icon} alt={section.caption} width="24" height="24" aria-hidden />
                )}
                <SectionTitle>{section.caption}</SectionTitle>
                <SectionText>{section.description}</SectionText>
                <SectionSize>
                  {section.size} {translations.t(section.key)}
                </SectionSize>
              </SectionCard>
            </Link>
          ))}
          <SupportCard>
            <SupportTitle>{translations.t('site.help.supportCard.title')}</SupportTitle>
            <SupportText>{translations.t('site.help.supportCard.description')}</SupportText>
            {IS_CLIENT && (
              <Button theme="secondary" size="S" width="100%" onClick={() => window.usedeskMessenger.open('chat')}>
                {translations.t('site.help.supportCard.button')}
              </Button>
            )}
          </SupportCard>
        </SectionWrap>
      )}
    </Container>
  );
};

const Container = styled.div`
  padding: 40px 16px 80px;
  ${textSize('body')};
  color: ${Colors.graphite};
  font-weight: 400;
  min-height: 100vh;

  @media ${Points.tablet_landscape} {
    padding: 40px 28px 100px;
  }

  @media ${Points.laptop_wide} {
    padding: 50px 28px 120px;
  }

  @media ${Points.fullhd} {
    padding: 60px 28px 160px;
  }
`;

const TopWrap = styled.div``;
const Title = styled.h1`
  margin-bottom: 20px;
  ${textSize('page_header')};
  color: ${Colors.main_black};
  font-weight: 200;
  text-align: center;

  @media ${Points.laptop_wide} {
    margin-bottom: 30px;
  }
`;

const Text = styled.p`
  margin: 0 auto;
  max-width: 535px;
  text-align: center;

  @media ${Points.fullhd} {
    max-width: 650px;
  }
`;

const SectionWrap = styled.section`
  display: flex;
  flex-direction: column;
  justify-content: center;
  gap: 15px;
  width: 312px;
  margin: 60px auto 0;

  @media ${Points.tablet_portrait} {
    display: grid;
    grid-template-columns: repeat(2, 312px);
    align-items: center;
  }

  @media ${Points.tablet_landscape} {
    grid-template-columns: repeat(3, 312px);
    margin-top: 70px;
  }

  @media ${Points.laptop_wide} {
    grid-template-columns: repeat(3, 350px);
    gap: 20px;
    margin-top: 80px;
  }

  @media ${Points.fullhd} {
    grid-template-columns: repeat(3, 365px);
    margin-top: 90px;
  }
`;

const SectionCard = styled.article`
  ${Mixins.resetLink()};
  display: flex;
  flex-direction: column;
  border: 1px solid #e1e1e1;
  padding: 25px 25px 27px;
  cursor: pointer;

  @media (hover: hover) and (pointer: fine) {
    &:hover {
      border-color: ${Colors.main_blue};
      box-shadow: 0px 8px 24px -4px #29308614, 0px 6px 12px -6px #2930861f;
    }
  }

  @media ${Points.tablet_landscape} {
    padding: 25px;
  }

  @media ${Points.laptop_wide} {
    padding: 30px 30px 33px;
  }

  @media ${Points.fullhd} {
    padding: 35px;
  }
`;

const SectionIcon = styled.img`
  margin-bottom: 20px;
`;

const SectionTitle = styled.h2`
  ${textSize('card_header')};
  margin-bottom: 10px;
  color: ${Colors.main_dark};
  font-weight: 300;

  @media ${Points.fullhd} {
    margin-bottom: 15px;
  }
`;

const SectionText = styled.p`
  ${textSize('body')};
`;

const SectionSize = styled.span`
  margin-top: 25px;
  font-weight: 400;
  color: ${Colors.second_gray};
  ${textSize('description')};

  @media ${Points.laptop_wide} {
    margin-top: 30px;
  }
`;

const SupportCard = styled.div`
  display: flex;
  flex-direction: column;
  padding: 20px;
  background-color: ${Colors.main_light_beige};
  background-image: url('/images/help/support-bg.png');
  background-repeat: no-repeat;
  background-position: 115% -22%;
  background-size: 116px 116px;
  box-sizing: border-box;

  @media ${Points.laptop_wide} {
    padding: 25px;
    background-position: 115% -30%;
    background-size: 124px 124px;
  }

  @media ${Points.fullhd} {
    padding: 30px;
    background-position: 115% -27%;
    background-size: 128px 128px;
  }

  & button {
    margin-top: 37px;
    align-self: end;
    background-color: transparent;

    @media ${Points.laptop_wide} {
      margin-top: 43px;
    }

    @media ${Points.fullhd} {
      margin-top: 41px;
    }
  }
`;

const SupportTitle = styled.h2`
  ${textSize('card_header')};
  color: ${Colors.main_dark};
  font-weight: 300;
  width: 60%;
  margin-bottom: 15px;

  @media ${Points.laptop_wide} {
    width: 65%;
  }

  @media ${Points.fullhd} {
    width: 80%;
  }
`;

const SupportText = styled.p`
  color: ${Colors.graphite};
  font-weight: 400;
  ${textSize('description')};
`;
